export interface Service {
  id: string;
  slug: string;
  title: string;
  shortDescription: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  category: 'social-media' | 'ai-tools' | 'analytics' | 'web-analytics' | 'advertising';
  features: string[];
  benefits: string[];
  metrics: string[];
  comingSoon?: boolean;
  pricing?: {
    plans?: string[];
  };
  seo: {
    metaDescription: string;
    keywords: string[];
    ogImage?: string;
  };
  content: string;
}

// Helper function to get services with translations
export const getServices = (i18nInstance?: any): Service[] => {
  let lang = 'tr';
  let translations: any = {};

  // Try to get language and translations from i18n instance
  if (i18nInstance) {
    lang = i18nInstance.language || 'tr';
    // Get translations from i18n instance - use 'services' namespace
    try {
      // Try store.data first (more reliable)
      if (i18nInstance.store?.data?.[lang]?.services) {
        translations = i18nInstance.store.data[lang].services;
      } else {
        // Fallback to getResourceBundle
        const resourceBundle = i18nInstance.getResourceBundle(lang, 'services');
        if (resourceBundle) {
          translations = resourceBundle;
        }
      }
    } catch (e) {
      // Fallback to require
      try {
        if (lang === 'en') {
          translations = require('../i18n/locales/pages/services/en.json');
        } else {
          translations = require('../i18n/locales/pages/services/tr.json');
        }
      } catch (e2) {
        console.warn('Translation file not found, using default');
      }
    }
  } else if (typeof window !== 'undefined') {
    try {
      // Try to get from i18n config
      const i18n = require('../i18n/config').default;
      if (i18n?.language) {
        lang = i18n.language;
      }
      // Get translations from i18n - use 'services' namespace
      if (i18n?.store?.data?.[lang]?.services) {
        translations = i18n.store.data[lang].services;
      } else {
        // Fallback to require
        if (lang === 'en') {
          translations = require('../i18n/locales/pages/services/en.json');
        } else {
          translations = require('../i18n/locales/pages/services/tr.json');
        }
      }
    } catch (e) {
      // Fallback to require
      try {
        if (lang === 'en') {
          translations = require('../i18n/locales/pages/services/en.json');
        } else {
          translations = require('../i18n/locales/pages/services/tr.json');
        }
      } catch (e2) {
        console.warn('Translation file not found, using default');
      }
    }
  }

  const servicesData = translations.items || {};

  return baseServices.map(baseService => {
    const translatedData = servicesData[baseService.id] || {};
    return {
      ...baseService,
      slug: translatedData.slug || baseService.slug,
      title: translatedData.title || baseService.title,
      shortDescription: translatedData.shortDescription || baseService.shortDescription,
      description: translatedData.description || baseService.description,
      features: translatedData.features || baseService.features,
      benefits: translatedData.benefits || baseService.benefits,
      metrics: translatedData.metrics || baseService.metrics,
      content: translatedData.content || baseService.content,
      seo: {
        ...baseService.seo,
        metaDescription: translatedData.seo?.metaDescription || baseService.seo.metaDescription,
        keywords: translatedData.seo?.keywords || baseService.seo.keywords,
        ogImage: translatedData.seo?.ogImage || baseService.seo.ogImage,
      },
    };
  });
};

// Base services structure (non-translatable data + fallback translations)
const baseServices: Service[] = [
  {
    id: 'instagram-analytics',
    slug: 'instagram-analitik',
    title: 'Instagram Analitik ve Raporlama Hizmeti',
    shortDescription: 'Instagram hesabınızın performansını detaylı analiz edin, takipçi davranışlarını anlayın ve içerik stratejinizi optimize edin.',
    description: 'Winnomi AI ile Instagram hesabınızın tüm metriklerini tek bir platformda toplayın. Takipçi analizi, içerik performansı, hashtag stratejisi ve daha fazlası.',
    icon: '📸',
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/20',
    category: 'social-media',
    features: [
      'Takipçi büyümesi ve demografik analizi',
      'Gönderi ve hikaye performans metrikleri',
      'Hashtag performans analizi',
      'En iyi paylaşım zamanları tespiti',
      'Rekabet analizi ve benchmark karşılaştırması',
      'Engagement oranı ve etkileşim trendleri',
      'Audience insights ve hedef kitle analizi',
      'IGTV ve Reels performans takibi'
    ],
    benefits: [
      'İçerik stratejinizi veriye dayalı optimize edin',
      'Takipçi kazanım maliyetlerinizi düşürün',
      'En etkili içerik formatlarını keşfedin',
      'Hashtag stratejinizi iyileştirin',
      'Rekabetinizden öne geçin',
      'ROI\'nizi artırın'
    ],
    metrics: [
      'Takipçi sayısı ve büyüme oranı',
      'Reach ve Impressions',
      'Engagement Rate',
      'Saves (Kaydetme) sayısı',
      'Profile Visits',
      'Website Clicks',
      'Story Views',
      'Reels ve IGTV performansı'
    ],
    pricing: {
      plans: ['Temel', 'Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'Instagram analitik ve raporlama hizmeti. Takipçi analizi, içerik performansı, hashtag stratejisi ve engagement metrikleri ile Instagram hesabınızı optimize edin.',
      keywords: ['Instagram analitik', 'Instagram raporlama', 'Instagram takipçi analizi', 'Instagram engagement', 'Instagram metrikleri', 'Instagram stratejisi', 'Instagram insights'],
      ogImage: '/services/instagram-analytics.png'
    },
    content: `
# Instagram Analitik ve Raporlama Hizmeti

Instagram, 2 milyardan fazla aktif kullanıcısı ile dünyanın en popüler sosyal medya platformlarından biridir. İşletmeniz için Instagram'da başarılı olmak, sadece içerik paylaşmakla değil, veriye dayalı stratejiler geliştirmekle mümkündür. Winnomi AI'ın Instagram analitik hizmeti ile hesabınızın performansını derinlemesine analiz edin ve stratejinizi optimize edin.

## Neden Instagram Analitiği Önemlidir?

Instagram analitiği, hesabınızın performansını ölçmek ve iyileştirmek için kritik öneme sahiptir. Doğru metrikleri takip etmek, içerik stratejinizi optimize etmenize, hedef kitlenizi daha iyi anlamanıza ve ROI'nizi artırmanıza yardımcı olur.

### Temel Metrikler

**Takipçi Analizi**
- Takipçi büyüme trendleri
- Demografik dağılım (yaş, cinsiyet, lokasyon)
- Takipçi kazanım kaynakları
- Takipçi kaybı analizi

**İçerik Performansı**
- Gönderi başına reach ve impressions
- Engagement oranları (beğeni, yorum, paylaşım)
- Saves (kaydetme) sayıları
- En iyi performans gösteren içerikler

**Hikaye Analizi**
- Hikaye görüntülenme sayıları
- Tıklama oranları
- Etkileşim özellikleri (poll, question, quiz)
- Hikaye kayıp oranları

## Özellikler

### 1. Detaylı Takipçi Analizi

Instagram hesabınızın takipçi profilini derinlemesine analiz edin:
- **Demografik Bilgiler**: Yaş, cinsiyet, lokasyon dağılımı
- **Aktif Saatler**: Takipçilerinizin en aktif olduğu saatler
- **İlgi Alanları**: Takipçilerinizin ilgilendiği konular
- **Büyüme Trendleri**: Zaman içindeki takipçi büyümesi

### 2. İçerik Performans Metrikleri

Hangi içeriklerin daha iyi performans gösterdiğini keşfedin:
- **Reach ve Impressions**: İçeriğinizin kaç kişiye ulaştığı
- **Engagement Rate**: Etkileşim oranları
- **Saves**: İçeriğinizin kaç kez kaydedildiği
- **Profile Visits**: Profil ziyaret sayıları

### 3. Hashtag Stratejisi

Hashtag performansınızı analiz edin:
- En etkili hashtag'lerinizi belirleyin
- Rekabet analizi yapın
- Trend hashtag'leri keşfedin
- Hashtag stratejinizi optimize edin

### 4. Optimal Yayın Zamanları

İçeriklerinizi en uygun zamanda yayınlayın:
- Takipçilerinizin en aktif olduğu saatleri tespit edin
- Zaman dilimi analizi
- Gün bazlı performans karşılaştırması

### 5. Reels ve IGTV Analizi

Video içeriklerinizin performansını takip edin:
- Reels görüntülenme sayıları
- IGTV performans metrikleri
- Video engagement oranları
- En popüler video içerikleriniz

## Faydalar

### Veriye Dayalı Kararlar

Instagram analitik verileri ile stratejinizi optimize edin:
- Hangi içerik türlerinin daha iyi performans gösterdiğini öğrenin
- En etkili paylaşım zamanlarını belirleyin
- Hashtag stratejinizi iyileştirin

### ROI Artışı

Performans metriklerini takip ederek:
- İçerik üretim maliyetlerinizi optimize edin
- Takipçi kazanım maliyetlerinizi düşürün
- Engagement oranlarınızı artırın

### Rekabet Analizi

Rakiplerinizi analiz edin:
- Rekabet performansını karşılaştırın
- Benchmark analizi yapın
- Sektör ortalamalarını görün

## Kullanım Senaryoları

### E-ticaret İşletmeleri

- Ürün gönderilerinin performansını takip edin
- Story'lerde ürün tanıtımlarının etkisini ölçün
- Influencer iş birliklerinin ROI'sini hesaplayın

### Markalar

- Marka bilinirliğini ölçün
- Kampanya performansını analiz edin
- Audience insights ile hedef kitleyi anlayın

### İçerik Üreticileri

- En popüler içerik formatlarını keşfedin
- Takipçi büyümesini optimize edin
- Engagement stratejilerini geliştirin

## Winnomi AI ile Instagram Analitiği

Winnomi AI, Instagram analitiğinizi bir üst seviyeye taşır:

- **Gerçek Zamanlı Veriler**: Anlık performans takibi
- **AI Destekli İçgörüler**: Yapay zeka ile otomatik öneriler
- **Kapsamlı Raporlama**: Detaylı ve anlaşılır raporlar
- **Kolay Entegrasyon**: Hızlı ve güvenli bağlantı

Instagram hesabınızı bağlayın ve performansınızı optimize etmeye başlayın!
    `
  },
  {
    id: 'facebook-analytics',
    slug: 'facebook-analitik',
    title: 'Facebook Analitik ve Sayfa Yönetimi Hizmeti',
    shortDescription: 'Facebook sayfa ve reklam performansınızı analiz edin, hedef kitlenizi anlayın ve stratejinizi optimize edin.',
    description: 'Facebook sayfa metrikleri, reklam performansı, audience insights ve içerik analizi ile Facebook stratejinizi güçlendirin.',
    icon: '📘',
    color: 'text-blue-600',
    bgColor: 'bg-blue-600/20',
    category: 'social-media',
    features: [
      'Sayfa performans metrikleri',
      'Gönderi ve video analizi',
      'Audience insights ve demografik analiz',
      'Reklam performans takibi',
      'Engagement ve reach analizi',
      'Video görüntülenme metrikleri',
      'Link tıklama analizi',
      'Rekabet analizi'
    ],
    benefits: [
      'Sayfa performansınızı optimize edin',
      'Hedef kitleyi daha iyi anlayın',
      'Reklam ROI\'nizi artırın',
      'İçerik stratejinizi iyileştirin',
      'Engagement oranlarınızı yükseltin'
    ],
    metrics: [
      'Page Likes ve Followers',
      'Post Reach ve Impressions',
      'Engagement Rate',
      'Video Views',
      'Link Clicks',
      'Page Views',
      'Post Engagement',
      'Audience Demographics'
    ],
    pricing: {
      plans: ['Temel', 'Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'Facebook analitik ve sayfa yönetimi hizmeti. Sayfa performansı, reklam analizi, audience insights ve engagement metrikleri ile Facebook stratejinizi optimize edin.',
      keywords: ['Facebook analitik', 'Facebook sayfa yönetimi', 'Facebook insights', 'Facebook reklam analizi', 'Facebook metrikleri', 'Facebook stratejisi'],
      ogImage: '/services/facebook-analytics.png'
    },
    content: `
# Facebook Analitik ve Sayfa Yönetimi Hizmeti

Facebook, 3 milyardan fazla aktif kullanıcısı ile dünyanın en büyük sosyal medya platformudur. İşletmeniz için Facebook'da başarılı olmak, doğru analitik araçları kullanmak ve veriye dayalı stratejiler geliştirmekle mümkündür.

## Facebook Analitiği Neden Önemlidir?

Facebook analitiği, sayfa performansınızı ölçmek, hedef kitlenizi anlamak ve stratejinizi optimize etmek için kritik öneme sahiptir. Doğru metrikleri takip etmek, içerik stratejinizi iyileştirmenize, reklam ROI'nizi artırmanıza ve marka bilinirliğinizi güçlendirmenize yardımcı olur.

## Temel Metrikler

### Sayfa Metrikleri

**Page Likes ve Followers**
- Sayfa beğeni sayısı ve büyüme trendleri
- Takipçi kazanım kaynakları
- Demografik dağılım

**Reach ve Impressions**
- Organik ve ücretli erişim
- Gönderi bazlı erişim analizi
- Zaman içindeki trendler

### İçerik Performansı

**Gönderi Metrikleri**
- Engagement oranları
- Beğeni, yorum, paylaşım sayıları
- Link tıklama oranları
- Video görüntülenme sayıları

**Video Analizi**
- Video görüntülenme süreleri
- Video tamamlama oranları
- Video engagement metrikleri

## Özellikler

### 1. Sayfa Performans Analizi

Facebook sayfanızın genel performansını analiz edin:
- Sayfa beğeni ve takipçi büyümesi
- Organik ve ücretli erişim karşılaştırması
- Sayfa ziyaret sayıları
- Sayfa etkileşimleri

### 2. Gönderi Performans Metrikleri

Her gönderinin performansını detaylı analiz edin:
- Reach ve impressions
- Engagement oranları
- Link tıklama sayıları
- Paylaşım ve yorum sayıları

### 3. Audience Insights

Hedef kitlenizi derinlemesine anlayın:
- Demografik bilgiler (yaş, cinsiyet, lokasyon)
- İlgi alanları ve davranışlar
- Aktif oldukları saatler
- Cihaz kullanım alışkanlıkları

### 4. Reklam Performans Takibi

Facebook reklamlarınızın performansını takip edin:
- Reklam erişimi ve gösterimleri
- Tıklama oranları (CTR)
- Dönüşüm oranları
- ROI hesaplamaları

### 5. Video Analizi

Video içeriklerinizin performansını ölçün:
- Video görüntülenme sayıları
- Ortalama görüntülenme süresi
- Video tamamlama oranları
- Video engagement metrikleri

## Faydalar

### Strateji Optimizasyonu

Facebook analitik verileri ile:
- En etkili içerik formatlarını keşfedin
- Optimal yayın zamanlarını belirleyin
- Hedef kitle stratejinizi iyileştirin

### ROI Artışı

Performans metriklerini takip ederek:
- Reklam harcamalarınızı optimize edin
- İçerik üretim maliyetlerinizi düşürün
- Engagement oranlarınızı artırın

### Marka Bilinirliği

Sayfa performansınızı artırarak:
- Marka bilinirliğinizi güçlendirin
- Topluluk oluşturun
- Müşteri sadakatini artırın

## Kullanım Senaryoları

### Küçük İşletmeler

- Yerel müşteri tabanınızı büyütün
- Etkinlik duyurularınızın etkisini ölçün
- Müşteri geri bildirimlerini takip edin

### E-ticaret

- Ürün tanıtımlarının performansını analiz edin
- Facebook Shop entegrasyonunu optimize edin
- Reklam kampanyalarının ROI'sini hesaplayın

### Hizmet Sektörü

- Hizmet tanıtımlarının etkisini ölçün
- Lead generation kampanyalarını optimize edin
- Müşteri hizmetleri etkileşimlerini takip edin

## Winnomi AI ile Facebook Analitiği

Winnomi AI, Facebook analitiğinizi bir üst seviyeye taşır:

- **Kapsamlı Metrikler**: Tüm Facebook metriklerini tek platformda
- **AI Destekli İçgörüler**: Otomatik öneriler ve tahminler
- **Kolay Raporlama**: Anlaşılır ve görsel raporlar
- **Güvenli Entegrasyon**: Facebook API ile güvenli bağlantı

Facebook sayfanızı bağlayın ve performansınızı optimize etmeye başlayın!
    `
  },
  {
    id: 'twitter-analytics',
    slug: 'twitter-analitik',
    title: 'Twitter Analitik ve Tweet Performans Hizmeti',
    shortDescription: 'Twitter hesabınızın performansını analiz edin, tweet stratejinizi optimize edin ve engagement oranlarınızı artırın.',
    description: 'Twitter metrikleri, tweet performansı, audience insights ve trend analizi ile Twitter stratejinizi güçlendirin.',
    icon: '🐦',
    color: 'text-sky-400',
    bgColor: 'bg-sky-400/20',
    category: 'social-media',
    features: [
      'Tweet performans metrikleri',
      'Engagement oranı analizi',
      'Hashtag ve mention takibi',
      'Audience insights',
      'Trend analizi',
      'Retweet ve like analizi',
      'Link tıklama takibi',
      'Rekabet analizi'
    ],
    benefits: [
      'Tweet stratejinizi optimize edin',
      'Engagement oranlarınızı artırın',
      'Trend konuları keşfedin',
      'Hedef kitleyi daha iyi anlayın',
      'Marka bilinirliğinizi güçlendirin'
    ],
    metrics: [
      'Tweet Impressions',
      'Engagement Rate',
      'Retweets',
      'Likes',
      'Replies',
      'Link Clicks',
      'Profile Clicks',
      'Follower Growth'
    ],
    pricing: {
      plans: ['Temel', 'Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'Twitter analitik ve tweet performans hizmeti. Tweet metrikleri, engagement analizi, hashtag takibi ve audience insights ile Twitter stratejinizi optimize edin.',
      keywords: ['Twitter analitik', 'Twitter metrikleri', 'Tweet performansı', 'Twitter engagement', 'Twitter stratejisi', 'Twitter insights'],
      ogImage: '/services/twitter-analytics.png'
    },
    content: `
# Twitter Analitik ve Tweet Performans Hizmeti

Twitter, 450 milyondan fazla aktif kullanıcısı ile gerçek zamanlı haberler, trendler ve sohbetlerin merkezidir. İşletmeniz için Twitter'da başarılı olmak, doğru içerik stratejisi ve veriye dayalı optimizasyon gerektirir.

## Twitter Analitiği Neden Önemlidir?

Twitter analitiği, tweet performansınızı ölçmek, hedef kitlenizi anlamak ve stratejinizi optimize etmek için kritik öneme sahiptir. Doğru metrikleri takip etmek, engagement oranlarınızı artırmanıza, trend konuları keşfetmenize ve marka bilinirliğinizi güçlendirmenize yardımcı olur.

## Temel Metrikler

### Tweet Performansı

**Impressions ve Reach**
- Tweet görüntülenme sayıları
- Organik ve ücretli erişim
- Zaman içindeki trendler

**Engagement Metrikleri**
- Retweet sayıları
- Like sayıları
- Reply sayıları
- Link tıklama oranları

### Audience Insights

**Takipçi Analizi**
- Takipçi büyüme trendleri
- Demografik dağılım
- İlgi alanları
- Aktif saatler

## Özellikler

### 1. Tweet Performans Analizi

Her tweet'in performansını detaylı analiz edin:
- Impressions ve reach
- Engagement oranları
- Retweet ve like sayıları
- Link tıklama oranları

### 2. Hashtag ve Mention Takibi

Hashtag ve mention performansınızı takip edin:
- En etkili hashtag'lerinizi belirleyin
- Mention sayılarını analiz edin
- Trend hashtag'leri keşfedin

### 3. Audience Insights

Hedef kitlenizi derinlemesine anlayın:
- Demografik bilgiler
- İlgi alanları
- Aktif oldukları saatler
- Cihaz kullanım alışkanlıkları

### 4. Trend Analizi

Güncel trendleri keşfedin:
- Popüler konular
- Trend hashtag'ler
- Sektör trendleri
- Rekabet analizi

### 5. Engagement Optimizasyonu

Engagement oranlarınızı artırın:
- En etkili tweet formatlarını keşfedin
- Optimal yayın zamanlarını belirleyin
- Call-to-action stratejilerini optimize edin

## Faydalar

### Strateji Optimizasyonu

Twitter analitik verileri ile:
- En etkili tweet formatlarını keşfedin
- Optimal yayın zamanlarını belirleyin
- Hashtag stratejinizi iyileştirin

### Engagement Artışı

Performans metriklerini takip ederek:
- Engagement oranlarınızı artırın
- Retweet sayılarınızı yükseltin
- Link tıklama oranlarınızı optimize edin

### Marka Bilinirliği

Twitter stratejinizi güçlendirerek:
- Marka bilinirliğinizi artırın
- Topluluk oluşturun
- Müşteri hizmetleri etkileşimlerini iyileştirin

## Kullanım Senaryoları

### Markalar

- Marka bilinirliğini artırın
- Müşteri hizmetleri etkileşimlerini takip edin
- Kampanya performansını analiz edin

### İçerik Üreticileri

- Tweet performansını optimize edin
- Takipçi büyümesini hızlandırın
- Engagement stratejilerini geliştirin

### Haber ve Medya

- Trend konuları keşfedin
- Breaking news etkileşimlerini takip edin
- Audience engagement'ı artırın

## Winnomi AI ile Twitter Analitiği

Winnomi AI, Twitter analitiğinizi bir üst seviyeye taşır:

- **Gerçek Zamanlı Veriler**: Anlık tweet performans takibi
- **AI Destekli İçgörüler**: Otomatik öneriler ve tahminler
- **Kapsamlı Raporlama**: Detaylı ve anlaşılır raporlar
- **Kolay Entegrasyon**: Twitter API ile güvenli bağlantı

Twitter hesabınızı bağlayın ve performansınızı optimize etmeye başlayın!
    `
  },
  {
    id: 'linkedin-analytics',
    slug: 'linkedin-analitik',
    title: 'LinkedIn Analitik ve Profesyonel Ağ Yönetimi Hizmeti',
    shortDescription: 'LinkedIn sayfa ve içerik performansınızı analiz edin, B2B pazarlama stratejinizi optimize edin ve profesyonel ağınızı büyütün.',
    description: 'LinkedIn metrikleri, içerik performansı, audience insights ve lead generation analizi ile LinkedIn stratejinizi güçlendirin.',
    icon: '💼',
    color: 'text-blue-700',
    bgColor: 'bg-blue-700/20',
    category: 'social-media',
    features: [
      'Sayfa performans metrikleri',
      'İçerik ve gönderi analizi',
      'Audience insights',
      'Lead generation takibi',
      'Engagement oranı analizi',
      'Video performans metrikleri',
      'Article analizi',
      'Rekabet analizi'
    ],
    benefits: [
      'B2B pazarlama stratejinizi optimize edin',
      'Lead generation süreçlerinizi iyileştirin',
      'Profesyonel ağınızı büyütün',
      'Thought leadership oluşturun',
      'ROI\'nizi artırın'
    ],
    metrics: [
      'Page Followers',
      'Post Impressions',
      'Engagement Rate',
      'Video Views',
      'Article Views',
      'Lead Generation',
      'Website Clicks',
      'Demographics'
    ],
    pricing: {
      plans: ['Temel', 'Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'LinkedIn analitik ve profesyonel ağ yönetimi hizmeti. Sayfa performansı, içerik analizi, lead generation ve B2B pazarlama metrikleri ile LinkedIn stratejinizi optimize edin.',
      keywords: ['LinkedIn analitik', 'LinkedIn sayfa yönetimi', 'B2B pazarlama', 'LinkedIn metrikleri', 'LinkedIn stratejisi', 'lead generation'],
      ogImage: '/services/linkedin-analytics.png'
    },
    content: `
# LinkedIn Analitik ve Profesyonel Ağ Yönetimi Hizmeti

LinkedIn, 900 milyondan fazla profesyonel kullanıcısı ile dünyanın en büyük profesyonel ağ platformudur. B2B pazarlama, lead generation ve profesyonel networking için LinkedIn'de başarılı olmak, doğru analitik araçları kullanmak ve veriye dayalı stratejiler geliştirmekle mümkündür.

## LinkedIn Analitiği Neden Önemlidir?

LinkedIn analitiği, sayfa performansınızı ölçmek, hedef kitlenizi anlamak ve B2B pazarlama stratejinizi optimize etmek için kritik öneme sahiptir. Doğru metrikleri takip etmek, lead generation süreçlerinizi iyileştirmenize, thought leadership oluşturmanıza ve ROI'nizi artırmanıza yardımcı olur.

## Temel Metrikler

### Sayfa Performansı

**Followers ve Reach**
- Sayfa takipçi sayısı ve büyüme trendleri
- Organik ve ücretli erişim
- Demografik dağılım

**İçerik Performansı**
- Gönderi impressions ve reach
- Engagement oranları
- Video görüntülenme sayıları
- Article okunma sayıları

### Lead Generation

**Lead Metrikleri**
- Lead form doldurma sayıları
- Lead kalitesi analizi
- Conversion oranları
- Cost per lead

## Özellikler

### 1. Sayfa Performans Analizi

LinkedIn sayfanızın genel performansını analiz edin:
- Takipçi büyüme trendleri
- Organik ve ücretli erişim karşılaştırması
- Sayfa görüntülenme sayıları
- Sayfa etkileşimleri

### 2. İçerik Performans Metrikleri

Her gönderinin performansını detaylı analiz edin:
- Impressions ve reach
- Engagement oranları
- Video görüntülenme sayıları
- Article okunma sayıları

### 3. Audience Insights

Hedef kitlenizi derinlemesine anlayın:
- Demografik bilgiler (iş unvanı, sektör, lokasyon)
- Şirket büyüklüğü dağılımı
- İlgi alanları
- Aktif oldukları saatler

### 4. Lead Generation Takibi

LinkedIn lead generation kampanyalarınızı takip edin:
- Lead form doldurma sayıları
- Lead kalitesi analizi
- Conversion oranları
- ROI hesaplamaları

### 5. Video ve Article Analizi

Video ve article içeriklerinizin performansını ölçün:
- Video görüntülenme sayıları
- Article okunma sayıları
- Engagement metrikleri
- Paylaşım sayıları

## Faydalar

### B2B Pazarlama Optimizasyonu

LinkedIn analitik verileri ile:
- En etkili içerik formatlarını keşfedin
- Optimal yayın zamanlarını belirleyin
- Hedef kitle stratejinizi iyileştirin

### Lead Generation İyileştirme

Performans metriklerini takip ederek:
- Lead generation kampanyalarınızı optimize edin
- Lead kalitesini artırın
- Cost per lead'i düşürün

### Thought Leadership

LinkedIn stratejinizi güçlendirerek:
- Thought leadership oluşturun
- Profesyonel ağınızı büyütün
- Marka bilinirliğinizi artırın

## Kullanım Senaryoları

### B2B Şirketler

- Lead generation kampanyalarını optimize edin
- Thought leadership içerikleri oluşturun
- Profesyonel ağınızı büyütün

### SaaS Şirketleri

- Ürün tanıtımlarının performansını analiz edin
- Webinar ve etkinlik duyurularını optimize edin
- Müşteri başarı hikayelerini paylaşın

### Danışmanlık Firmaları

- Uzmanlık içeriklerinin etkisini ölçün
- Case study'lerin performansını analiz edin
- Profesyonel ağınızı genişletin

## Winnomi AI ile LinkedIn Analitiği

Winnomi AI, LinkedIn analitiğinizi bir üst seviyeye taşır:

- **Kapsamlı Metrikler**: Tüm LinkedIn metriklerini tek platformda
- **AI Destekli İçgörüler**: Otomatik öneriler ve tahminler
- **Lead Generation Analizi**: Detaylı lead takibi ve analizi
- **Kolay Entegrasyon**: LinkedIn API ile güvenli bağlantı

LinkedIn sayfanızı bağlayın ve performansınızı optimize etmeye başlayın!
    `
  },
  {
    id: 'tiktok-analytics',
    slug: 'tiktok-analitik',
    title: 'TikTok Analitik ve Video Performans Hizmeti',
    shortDescription: 'TikTok hesabınızın performansını analiz edin, video stratejinizi optimize edin ve viral potansiyelinizi artırın.',
    description: 'TikTok metrikleri, video performansı, trend analizi ve audience insights ile TikTok stratejinizi güçlendirin.',
    icon: '🎵',
    color: 'text-black',
    bgColor: 'bg-white/20',
    category: 'social-media',
    features: [
      'Video performans metrikleri',
      'Engagement oranı analizi',
      'Trend analizi ve hashtag takibi',
      'Audience insights',
      'Viral potansiyel analizi',
      'Müzik ve ses analizi',
      'Video tamamlama oranları',
      'Rekabet analizi'
    ],
    benefits: [
      'Video stratejinizi optimize edin',
      'Viral potansiyelinizi artırın',
      'Trend konuları keşfedin',
      'Engagement oranlarınızı yükseltin',
      'Takipçi büyümesini hızlandırın'
    ],
    metrics: [
      'Video Views',
      'Engagement Rate',
      'Likes',
      'Comments',
      'Shares',
      'Video Completion Rate',
      'Profile Views',
      'Follower Growth'
    ],
    pricing: {
      plans: ['Temel', 'Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'TikTok analitik ve video performans hizmeti. Video metrikleri, engagement analizi, trend takibi ve viral potansiyel analizi ile TikTok stratejinizi optimize edin.',
      keywords: ['TikTok analitik', 'TikTok metrikleri', 'TikTok video performansı', 'TikTok engagement', 'TikTok stratejisi', 'viral içerik'],
      ogImage: '/services/tiktok-analytics.png'
    },
    content: `
# TikTok Analitik ve Video Performans Hizmeti

TikTok, 1 milyardan fazla aktif kullanıcısı ile dünyanın en hızlı büyüyen sosyal medya platformlarından biridir. Kısa video içerikleri, trend müzikler ve yaratıcı formatlarla TikTok'da başarılı olmak, doğru analitik araçları kullanmak ve veriye dayalı stratejiler geliştirmekle mümkündür.

## TikTok Analitiği Neden Önemlidir?

TikTok analitiği, video performansınızı ölçmek, trend konuları keşfetmek ve stratejinizi optimize etmek için kritik öneme sahiptir. Doğru metrikleri takip etmek, viral potansiyelinizi artırmanıza, engagement oranlarınızı yükseltmenize ve takipçi büyümenizi hızlandırmanıza yardımcı olur.

## Temel Metrikler

### Video Performansı

**Video Views ve Reach**
- Video görüntülenme sayıları
- Organik ve ücretli erişim
- Video tamamlama oranları
- Zaman içindeki trendler

**Engagement Metrikleri**
- Like sayıları
- Comment sayıları
- Share sayıları
- Profile ziyaret sayıları

### Audience Insights

**Takipçi Analizi**
- Takipçi büyüme trendleri
- Demografik dağılım
- İlgi alanları
- Aktif saatler

## Özellikler

### 1. Video Performans Analizi

Her videonun performansını detaylı analiz edin:
- Video görüntülenme sayıları
- Video tamamlama oranları
- Engagement oranları
- Viral potansiyel analizi

### 2. Trend Analizi

Güncel trendleri keşfedin:
- Popüler hashtag'ler
- Trend müzikler
- Viral formatlar
- Sektör trendleri

### 3. Hashtag ve Müzik Analizi

Hashtag ve müzik performansınızı takip edin:
- En etkili hashtag'lerinizi belirleyin
- Trend müzikleri keşfedin
- Müzik performans analizi

### 4. Audience Insights

Hedef kitlenizi derinlemesine anlayın:
- Demografik bilgiler
- İlgi alanları
- Aktif oldukları saatler
- Video tüketim alışkanlıkları

### 5. Viral Potansiyel Analizi

Videolarınızın viral potansiyelini analiz edin:
- Viral potansiyel skorları
- Trend uyumu analizi
- Engagement tahminleri
- Optimizasyon önerileri

## Faydalar

### Strateji Optimizasyonu

TikTok analitik verileri ile:
- En etkili video formatlarını keşfedin
- Optimal yayın zamanlarını belirleyin
- Trend konularına uyum sağlayın

### Viral Potansiyel Artışı

Performans metriklerini takip ederek:
- Viral potansiyelinizi artırın
- Trend konularına hızlı uyum sağlayın
- Engagement oranlarınızı yükseltin

### Takipçi Büyümesi

TikTok stratejinizi güçlendirerek:
- Takipçi büyümenizi hızlandırın
- Topluluk oluşturun
- Marka bilinirliğinizi artırın

## Kullanım Senaryoları

### Markalar

- Marka bilinirliğini artırın
- Ürün tanıtımlarının performansını analiz edin
- Influencer iş birliklerinin etkisini ölçün

### İçerik Üreticileri

- Video performansını optimize edin
- Takipçi büyümesini hızlandırın
- Viral içerik stratejilerini geliştirin

### E-ticaret

- Ürün tanıtım videolarının etkisini ölçün
- Trend konularına uyum sağlayın
- Satış dönüşümlerini artırın

## Winnomi AI ile TikTok Analitiği

Winnomi AI, TikTok analitiğinizi bir üst seviyeye taşır:

- **Gerçek Zamanlı Veriler**: Anlık video performans takibi
- **AI Destekli İçgörüler**: Otomatik öneriler ve tahminler
- **Trend Analizi**: Güncel trendleri keşfedin
- **Kolay Entegrasyon**: TikTok API ile güvenli bağlantı

TikTok hesabınızı bağlayın ve performansınızı optimize etmeye başlayın!
    `
  },
  {
    id: 'ai-predictions',
    slug: 'ai-ongoruleri',
    title: 'AI Öngörüleri ve Tahmin Analitiği Hizmeti',
    shortDescription: 'Yapay zeka destekli tahminler ile sosyal medya performansınızın geleceğini öngörün ve stratejinizi optimize edin.',
    description: 'Makine öğrenmesi algoritmaları ile performans tahminleri, trend öngörüleri ve strateji önerileri alın.',
    icon: '🧠',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/20',
    category: 'ai-tools',
    features: [
      'Performans tahminleri',
      'Trend öngörüleri',
      'Engagement tahminleri',
      'Takipçi büyüme projeksiyonları',
      'Optimal yayın zamanı önerileri',
      'İçerik performans tahminleri',
      'ROI projeksiyonları',
      'Risk analizi'
    ],
    benefits: [
      'Gelecek performansı öngörün',
      'Stratejinizi proaktif olarak optimize edin',
      'Riski minimize edin',
      'ROI\'nizi maksimize edin',
      'Rekabet avantajı kazanın'
    ],
    metrics: [
      'Tahmin Doğruluğu',
      'Performans Projeksiyonları',
      'Trend Tahminleri',
      'Risk Skorları',
      'Optimizasyon Önerileri'
    ],
    pricing: {
      plans: ['Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'AI öngörüleri ve tahmin analitiği hizmeti. Yapay zeka destekli performans tahminleri, trend öngörüleri ve strateji önerileri ile sosyal medya stratejinizi optimize edin.',
      keywords: ['AI öngörüleri', 'yapay zeka tahmin', 'performans tahmini', 'makine öğrenmesi', 'AI analitik', 'tahmin analitiği'],
      ogImage: '/services/ai-predictions.png'
    },
    content: `
# AI Öngörüleri ve Tahmin Analitiği Hizmeti

Yapay zeka teknolojileri, sosyal medya analitiğini devrim niteliğinde değiştiriyor. Winnomi AI'ın AI öngörüleri hizmeti ile performansınızın geleceğini öngörün, trendleri keşfedin ve stratejinizi proaktif olarak optimize edin.

## AI Öngörüleri Nedir?

AI öngörüleri, makine öğrenmesi algoritmaları kullanarak geçmiş verilerinizi analiz eden ve gelecek performansınızı tahmin eden bir hizmettir. Bu teknoloji, sosyal medya stratejinizi proaktif olarak optimize etmenize ve rekabet avantajı kazanmanıza yardımcı olur.

## Nasıl Çalışır?

### Veri Toplama

AI algoritmaları, geçmiş performans verilerinizi toplar:
- İçerik performans metrikleri
- Engagement trendleri
- Takipçi büyüme verileri
- Zamanlama analizleri

### Model Eğitimi

Makine öğrenmesi modelleri, verilerinizi analiz eder:
- Pattern recognition (desen tanıma)
- Trend analizi
- Anomali tespiti
- Korelasyon analizi

### Tahmin Üretimi

Eğitilmiş modeller, gelecek performansınızı tahmin eder:
- Performans projeksiyonları
- Trend öngörüleri
- Risk analizleri
- Optimizasyon önerileri

## Özellikler

### 1. Performans Tahminleri

Gelecek performansınızı öngörün:
- Engagement oranı tahminleri
- Takipçi büyüme projeksiyonları
- Reach ve impressions tahminleri
- ROI projeksiyonları

### 2. Trend Öngörüleri

Güncel trendleri keşfedin:
- Popüler konu tahminleri
- Hashtag trend öngörüleri
- Format trend analizi
- Sektör trend projeksiyonları

### 3. Optimal Zamanlama

En iyi yayın zamanlarını belirleyin:
- Optimal yayın saatleri
- Gün bazlı performans tahminleri
- Zaman dilimi analizi
- Trend uyum önerileri

### 4. İçerik Performans Tahminleri

İçeriklerinizin performansını öngörün:
- Viral potansiyel analizi
- Engagement tahminleri
- Reach projeksiyonları
- Optimizasyon önerileri

### 5. Risk Analizi

Potansiyel riskleri tespit edin:
- Performans düşüş riskleri
- Trend değişim uyarıları
- Rekabet risk analizi
- Mitigasyon önerileri

## Faydalar

### Proaktif Strateji

AI öngörüleri ile:
- Gelecek performansı öngörün
- Stratejinizi proaktif olarak optimize edin
- Riski minimize edin

### ROI Maksimizasyonu

Performans tahminleri ile:
- ROI projeksiyonları yapın
- Yatırım kararlarınızı optimize edin
- Kaynak tahsisini iyileştirin

### Rekabet Avantajı

Trend öngörüleri ile:
- Trend konularına erken uyum sağlayın
- Rekabetinizden öne geçin
- İnovasyon fırsatlarını keşfedin

## Kullanım Senaryoları

### İçerik Planlama

- Gelecek içeriklerin performansını öngörün
- Optimal yayın zamanlarını belirleyin
- Trend konularına uyum sağlayın

### Kampanya Optimizasyonu

- Kampanya performansını tahmin edin
- Bütçe tahsisini optimize edin
- ROI projeksiyonları yapın

### Strateji Geliştirme

- Uzun vadeli stratejiler geliştirin
- Risk analizi yapın
- Fırsatları keşfedin

## Winnomi AI ile AI Öngörüleri

Winnomi AI'ın AI öngörüleri hizmeti:

- **Gelişmiş Algoritmalar**: En son makine öğrenmesi teknolojileri
- **Yüksek Doğruluk**: %85+ tahmin doğruluğu
- **Gerçek Zamanlı Güncellemeler**: Sürekli öğrenen modeller
- **Kolay Kullanım**: Anlaşılır ve görsel raporlar

AI öngörüleri ile stratejinizi bir üst seviyeye taşıyın!
    `
  },
  {
    id: 'ai-recommendations',
    slug: 'ai-onerileri',
    title: 'AI Önerileri ve Strateji Optimizasyonu Hizmeti',
    shortDescription: 'Yapay zeka destekli öneriler ile sosyal medya stratejinizi optimize edin, içerik önerileri alın ve performansınızı artırın.',
    description: 'AI destekli içerik önerileri, hashtag önerileri, optimal yayın zamanları ve strateji önerileri ile performansınızı maksimize edin.',
    icon: '✨',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/20',
    category: 'ai-tools',
    features: [
      'İçerik önerileri',
      'Hashtag önerileri',
      'Optimal yayın zamanı önerileri',
      'Strateji önerileri',
      'A/B test önerileri',
      'Rekabet analizi önerileri',
      'Audience targeting önerileri',
      'ROI optimizasyon önerileri'
    ],
    benefits: [
      'İçerik stratejinizi optimize edin',
      'Engagement oranlarınızı artırın',
      'Zaman ve kaynak tasarrufu sağlayın',
      'ROI\'nizi maksimize edin',
      'Rekabet avantajı kazanın'
    ],
    metrics: [
      'Öneri Kabul Oranı',
      'Performans İyileştirme',
      'ROI Artışı',
      'Zaman Tasarrufu',
      'Engagement Artışı'
    ],
    pricing: {
      plans: ['Pro', 'Enterprise']
    },
    seo: {
      metaDescription: 'AI önerileri ve strateji optimizasyonu hizmeti. Yapay zeka destekli içerik önerileri, hashtag önerileri ve strateji önerileri ile sosyal medya performansınızı artırın.',
      keywords: ['AI önerileri', 'yapay zeka önerileri', 'içerik önerileri', 'AI strateji', 'sosyal medya optimizasyonu', 'AI pazarlama'],
      ogImage: '/services/ai-recommendations.png'
    },
    content: `
# AI Önerileri ve Strateji Optimizasyonu Hizmeti

Yapay zeka teknolojileri, sosyal medya stratejinizi optimize etmenize yardımcı olur. Winnomi AI'ın AI önerileri hizmeti ile içerik önerileri alın, hashtag stratejinizi iyileştirin ve performansınızı maksimize edin.

## AI Önerileri Nedir?

AI önerileri, makine öğrenmesi algoritmaları kullanarak performans verilerinizi analiz eden ve size özel strateji önerileri sunan bir hizmettir. Bu teknoloji, içerik stratejinizi optimize etmenize, engagement oranlarınızı artırmanıza ve ROI'nizi maksimize etmenize yardımcı olur.

## Nasıl Çalışır?

### Veri Analizi

AI algoritmaları, performans verilerinizi analiz eder:
- İçerik performans metrikleri
- Engagement trendleri
- Audience insights
- Rekabet analizi

### Öneri Üretimi

Analiz sonuçlarına göre öneriler üretilir:
- İçerik önerileri
- Hashtag önerileri
- Zamanlama önerileri
- Strateji önerileri

### Sürekli Öğrenme

AI, önerilerinizin sonuçlarını öğrenir:
- Öneri performans takibi
- Model iyileştirme
- Kişiselleştirme
- Optimizasyon

## Özellikler

### 1. İçerik Önerileri

Size özel içerik önerileri alın:
- Konu önerileri
- Format önerileri
- Ton önerileri
- Görsel önerileri

### 2. Hashtag Önerileri

Etkili hashtag stratejisi geliştirin:
- Trend hashtag önerileri
- Niche hashtag önerileri
- Rekabet analizi
- Performans tahminleri

### 3. Optimal Zamanlama

En iyi yayın zamanlarını belirleyin:
- Optimal yayın saatleri
- Gün bazlı öneriler
- Zaman dilimi analizi
- Trend uyum önerileri

### 4. Strateji Önerileri

Uzun vadeli strateji önerileri alın:
- Kampanya önerileri
- İçerik takvimi önerileri
- Audience targeting önerileri
- ROI optimizasyon önerileri

### 5. A/B Test Önerileri

Test edilmesi gereken varyasyonları keşfedin:
- İçerik varyasyon önerileri
- Hashtag kombinasyon önerileri
- Zamanlama test önerileri
- Format test önerileri

## Faydalar

### Zaman Tasarrufu

AI önerileri ile:
- Manuel analiz süresini azaltın
- Hızlı karar verin
- Kaynak tahsisini optimize edin

### Performans Artışı

Önerileri uygulayarak:
- Engagement oranlarınızı artırın
- Reach ve impressions'ı yükseltin
- ROI'nizi maksimize edin

### Rekabet Avantajı

AI destekli stratejiler ile:
- Rekabetinizden öne geçin
- İnovasyon fırsatlarını keşfedin
- Trend konularına erken uyum sağlayın

## Kullanım Senaryoları

### İçerik Planlama

- İçerik konularını keşfedin
- Format önerileri alın
- Optimal yayın zamanlarını belirleyin

### Hashtag Stratejisi

- Etkili hashtag'ler keşfedin
- Rekabet analizi yapın
- Performans tahminleri alın

### Kampanya Optimizasyonu

- Kampanya önerileri alın
- Audience targeting optimize edin
- ROI optimizasyon önerileri uygulayın

## Winnomi AI ile AI Önerileri

Winnomi AI'ın AI önerileri hizmeti:

- **Kişiselleştirilmiş Öneriler**: Size özel strateji önerileri
- **Gerçek Zamanlı Güncellemeler**: Sürekli öğrenen modeller
- **Yüksek Başarı Oranı**: %80+ öneri kabul oranı
- **Kolay Uygulama**: Tek tıkla önerileri uygulayın

AI önerileri ile stratejinizi bir üst seviyeye taşıyın!
    `
  },
  {
    id: 'web-traffic-analytics',
    slug: 'web-trafik-analitik',
    title: 'Web Trafiği Analitik Hizmeti',
    shortDescription: 'Web sitenizin trafik metriklerini analiz edin, ziyaretçi davranışlarını takip edin ve trafik kaynaklarını optimize edin.',
    description: 'Web sitenizin trafik performansını detaylı analiz edin. Ziyaretçi sayıları, trafik kaynakları, sayfa görüntülemeleri ve daha fazlası.',
    icon: '🌐',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Ziyaretçi sayısı ve büyüme trendleri',
      'Trafik kaynakları analizi',
      'Sayfa görüntülenme metrikleri',
      'Oturum süresi ve sayfa başına görüntülenme',
      'Çıkış oranı analizi',
      'Yeni ve dönen ziyaretçi analizi',
      'Cihaz ve tarayıcı analizi',
      'Coğrafi trafik dağılımı'
    ],
    benefits: [
      'Trafik kaynaklarınızı optimize edin',
      'Ziyaretçi davranışlarını anlayın',
      'Sayfa performansını iyileştirin',
      'Dönüşüm oranlarınızı artırın',
      'SEO stratejinizi güçlendirin'
    ],
    metrics: [
      'Toplam Ziyaretçi',
      'Sayfa Görüntülenme',
      'Oturum Süresi',
      'Çıkış Oranı',
      'Yeni Ziyaretçi Oranı',
      'Trafik Kaynakları',
      'Coğrafi Dağılım',
      'Cihaz Türleri'
    ],
    seo: {
      metaDescription: 'Web trafiği analitik hizmeti. Ziyaretçi analizi, trafik kaynakları, sayfa performansı ve davranışsal metrikler ile web sitenizin trafiğini optimize edin.',
      keywords: ['web trafiği', 'trafik analizi', 'ziyaretçi analizi', 'web analitik', 'sayfa performansı', 'trafik kaynakları'],
      ogImage: '/services/web-traffic-analytics.png'
    },
    content: `
# Web Trafiği Analitik Hizmeti

Web sitenizin trafik performansını detaylı analiz edin ve ziyaretçi davranışlarını anlayın. Winnomi AI ile trafik kaynaklarınızı optimize edin ve dönüşüm oranlarınızı artırın.

## Web Trafiği Analitiği Nedir?

Web trafiği analitiği, web sitenize gelen ziyaretçilerin sayısını, davranışlarını ve trafik kaynaklarını analiz etme sürecidir. Bu analiz, web sitenizin performansını ölçmek ve iyileştirmek için kritik öneme sahiptir.

## Temel Metrikler

### Ziyaretçi Metrikleri
- Toplam ziyaretçi sayısı
- Yeni ve dönen ziyaretçiler
- Benzersiz ziyaretçiler
- Ziyaretçi büyüme trendleri

### Sayfa Performansı
- Sayfa görüntülenme sayıları
- Ortalama oturum süresi
- Sayfa başına görüntülenme
- Çıkış oranları

### Trafik Kaynakları
- Organik arama
- Direkt trafik
- Sosyal medya
- Referral trafik
- E-posta kampanyaları

## Özellikler

### 1. Trafik Kaynakları Analizi

Web sitenize gelen trafiğin kaynaklarını analiz edin:
- Organik arama performansı
- Sosyal medya trafiği
- Direkt trafik analizi
- Referral trafik kaynakları

### 2. Ziyaretçi Davranış Analizi

Ziyaretçilerinizin davranışlarını anlayın:
- Sayfa görüntülenme süreleri
- Tıklama yolları
- Çıkış noktaları
- Dönüşüm hunileri

### 3. Coğrafi Analiz

Ziyaretçilerinizin coğrafi dağılımını görün:
- Ülke ve şehir bazlı trafik
- Dil tercihleri
- Zaman dilimi analizi

### 4. Cihaz ve Tarayıcı Analizi

Ziyaretçilerinizin kullandığı cihazları analiz edin:
- Masaüstü vs mobil trafik
- Tarayıcı dağılımı
- İşletim sistemi analizi
- Ekran çözünürlüğü

## Faydalar

### Trafik Optimizasyonu

Web trafiği analitiği ile:
- En etkili trafik kaynaklarınızı belirleyin
- Trafik kaynaklarınızı çeşitlendirin
- Organik trafiğinizi artırın

### Dönüşüm İyileştirme

Ziyaretçi davranış analizi ile:
- Dönüşüm hunilerini optimize edin
- Çıkış oranlarını düşürün
- Kullanıcı deneyimini iyileştirin

## Kullanım Senaryoları

### E-ticaret
- Ürün sayfalarının performansını analiz edin
- Satın alma hunilerini optimize edin
- Trafik kaynaklarının ROI'sini hesaplayın

### Kurumsal Web Siteleri
- Lead generation trafiğini takip edin
- İçerik performansını analiz edin
- SEO stratejinizi optimize edin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-engagement-analytics',
    slug: 'web-etkilesim-analitik',
    title: 'Web Etkileşim Analitik Hizmeti',
    shortDescription: 'Web sitenizdeki kullanıcı etkileşimlerini ölçün, kullanıcı deneyimini analiz edin ve engagement oranlarınızı optimize edin.',
    description: 'Kullanıcıların web sitenizle nasıl etkileşim kurduğunu analiz edin. Tıklama oranları, scroll derinliği, form doldurma ve daha fazlası.',
    icon: '👆',
    color: 'text-green-500',
    bgColor: 'bg-green-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Tıklama oranları (CTR)',
      'Scroll derinliği analizi',
      'Form doldurma oranları',
      'Video oynatma metrikleri',
      'Buton tıklama analizi',
      'Hover ve focus olayları',
      'Sayfa içi navigasyon',
      'Etkileşim süreleri'
    ],
    benefits: [
      'Kullanıcı deneyimini iyileştirin',
      'Etkileşim oranlarınızı artırın',
      'Dönüşüm oranlarınızı optimize edin',
      'İçerik performansını ölçün',
      'A/B test sonuçlarını analiz edin'
    ],
    metrics: [
      'Tıklama Oranı (CTR)',
      'Scroll Derinliği',
      'Form Doldurma Oranı',
      'Video Oynatma Oranı',
      'Ortalama Etkileşim Süresi',
      'Sayfa İçi Navigasyon',
      'Buton Tıklama Oranları',
      'Hover Olayları'
    ],
    seo: {
      metaDescription: 'Web etkileşim analitik hizmeti. Kullanıcı etkileşimleri, tıklama oranları, scroll derinliği ve form doldurma metrikleri ile web sitenizin engagement\'ını optimize edin.',
      keywords: ['web etkileşim', 'kullanıcı etkileşimi', 'CTR', 'scroll analizi', 'form analizi', 'engagement metrikleri'],
      ogImage: '/services/web-engagement-analytics.png'
    },
    content: `
# Web Etkileşim Analitik Hizmeti

Web sitenizdeki kullanıcı etkileşimlerini detaylı analiz edin ve kullanıcı deneyimini optimize edin. Winnomi AI ile engagement oranlarınızı artırın ve dönüşümlerinizi maksimize edin.

## Web Etkileşim Analitiği Nedir?

Web etkileşim analitiği, kullanıcıların web sitenizle nasıl etkileşim kurduğunu ölçme ve analiz etme sürecidir. Bu analiz, kullanıcı deneyimini iyileştirmek ve dönüşüm oranlarını artırmak için kritik öneme sahiptir.

## Temel Metrikler

### Tıklama Metrikleri
- Buton tıklama oranları
- Link tıklama oranları
- CTA tıklama performansı
- Menü tıklama analizi

### Scroll ve Navigasyon
- Scroll derinliği
- Sayfa içi navigasyon
- Hover olayları
- Focus olayları

### Form ve Dönüşüm
- Form doldurma oranları
- Form terk oranları
- Dönüşüm hunileri
- A/B test sonuçları

## Özellikler

### 1. Tıklama Analizi

Kullanıcıların tıklama davranışlarını analiz edin:
- Buton tıklama oranları
- Link tıklama performansı
- CTA etkinliği
- Menü kullanımı

### 2. Scroll Analizi

Kullanıcıların scroll davranışlarını ölçün:
- Scroll derinliği
- Zaman bazlı scroll analizi
- İçerik görünürlüğü
- Dikkat haritaları

### 3. Form Analizi

Form performansınızı analiz edin:
- Form doldurma oranları
- Form terk noktaları
- Alan bazlı analiz
- Hata oranları

### 4. Video ve Medya Analizi

Video ve medya içeriklerinizin performansını ölçün:
- Video oynatma oranları
- Ortalama izlenme süresi
- Tamamlama oranları
- Etkileşim metrikleri

## Faydalar

### Kullanıcı Deneyimi İyileştirme

Etkileşim analizi ile:
- Kullanıcı deneyimini optimize edin
- Etkileşim noktalarını iyileştirin
- Navigasyonu kolaylaştırın

### Dönüşüm Optimizasyonu

Etkileşim metrikleri ile:
- Dönüşüm oranlarınızı artırın
- Form performansını iyileştirin
- CTA etkinliğini optimize edin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-pages-analytics',
    slug: 'web-sayfalar-analitik',
    title: 'Web Sayfaları Performans Analitik Hizmeti',
    shortDescription: 'Web sitenizdeki sayfaların performansını analiz edin, en popüler sayfaları keşfedin ve sayfa optimizasyonu yapın.',
    description: 'Her sayfanın performansını detaylı analiz edin. Sayfa görüntülenmeleri, oturum süreleri, çıkış oranları ve daha fazlası.',
    icon: '📄',
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Sayfa bazlı görüntülenme analizi',
      'Sayfa performans karşılaştırması',
      'Çıkış oranı analizi',
      'Ortalama oturum süresi',
      'Sayfa yükleme hızı analizi',
      'İçerik performans metrikleri',
      'Sayfa içi navigasyon',
      'Landing page analizi'
    ],
    benefits: [
      'En popüler sayfalarınızı keşfedin',
      'Sayfa performansını optimize edin',
      'İçerik stratejinizi iyileştirin',
      'SEO performansını artırın',
      'Dönüşüm oranlarınızı yükseltin'
    ],
    metrics: [
      'Sayfa Görüntülenme',
      'Benzersiz Görüntülenme',
      'Ortalama Oturum Süresi',
      'Çıkış Oranı',
      'Yükleme Hızı',
      'Sayfa Değeri',
      'Dönüşüm Oranı',
      'Bounce Rate'
    ],
    seo: {
      metaDescription: 'Web sayfaları performans analitik hizmeti. Sayfa bazlı metrikler, performans karşılaştırması ve içerik analizi ile web sitenizin sayfa performansını optimize edin.',
      keywords: ['sayfa analitik', 'sayfa performansı', 'landing page analizi', 'içerik performansı', 'sayfa metrikleri'],
      ogImage: '/services/web-pages-analytics.png'
    },
    content: `
# Web Sayfaları Performans Analitik Hizmeti

Web sitenizdeki her sayfanın performansını detaylı analiz edin ve içerik stratejinizi optimize edin. Winnomi AI ile en popüler sayfalarınızı keşfedin ve sayfa performansınızı artırın.

## Web Sayfaları Analitiği Nedir?

Web sayfaları analitiği, web sitenizdeki her sayfanın performansını ölçme ve analiz etme sürecidir. Bu analiz, içerik stratejinizi optimize etmek ve kullanıcı deneyimini iyileştirmek için kritik öneme sahiptir.

## Temel Metrikler

### Sayfa Performansı
- Sayfa görüntülenme sayıları
- Benzersiz görüntülenmeler
- Ortalama oturum süresi
- Çıkış oranları

### İçerik Metrikleri
- İçerik performansı
- Okunma süreleri
- Paylaşım sayıları
- Yorum sayıları

## Özellikler

### 1. Sayfa Bazlı Analiz

Her sayfanın performansını detaylı analiz edin:
- Görüntülenme sayıları
- Oturum süreleri
- Çıkış oranları
- Dönüşüm oranları

### 2. Performans Karşılaştırması

Sayfalarınızı birbiriyle karşılaştırın:
- En popüler sayfalar
- En yüksek engagement
- En düşük çıkış oranı
- En yüksek dönüşüm

### 3. Landing Page Analizi

Landing page'lerinizin performansını analiz edin:
- Trafik kaynakları
- Dönüşüm oranları
- A/B test sonuçları
- Optimizasyon önerileri

## Faydalar

### İçerik Optimizasyonu

Sayfa analitiği ile:
- En popüler içeriklerinizi keşfedin
- İçerik stratejinizi optimize edin
- SEO performansını artırın

### Dönüşüm İyileştirme

Sayfa performans analizi ile:
- Dönüşüm oranlarınızı artırın
- Landing page'leri optimize edin
- Kullanıcı deneyimini iyileştirin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-events-analytics',
    slug: 'web-etkinlikler-analitik',
    title: 'Web Etkinlikleri Takip ve Analitik Hizmeti',
    shortDescription: 'Web sitenizdeki kullanıcı etkinliklerini takip edin, özel etkinlikler oluşturun ve dönüşümlerinizi analiz edin.',
    description: 'Kullanıcı etkinliklerini detaylı takip edin. Buton tıklamaları, form gönderimleri, video oynatmalar ve özel etkinlikler.',
    icon: '⚡',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Özel etkinlik takibi',
      'Buton tıklama takibi',
      'Form gönderim takibi',
      'Video oynatma takibi',
      'Dosya indirme takibi',
      'Etkinlik bazlı dönüşüm analizi',
      'Etkinlik hunileri',
      'Gerçek zamanlı etkinlik takibi'
    ],
    benefits: [
      'Kullanıcı davranışlarını anlayın',
      'Dönüşüm hunilerini optimize edin',
      'Etkinlik bazlı segmentasyon yapın',
      'A/B test sonuçlarını ölçün',
      'ROI hesaplamalarınızı iyileştirin'
    ],
    metrics: [
      'Etkinlik Sayısı',
      'Etkinlik Oranı',
      'Benzersiz Etkinlik',
      'Etkinlik Değeri',
      'Dönüşüm Oranı',
      'Etkinlik Hunileri',
      'Etkinlik Trendleri',
      'Segment Bazlı Etkinlik'
    ],
    seo: {
      metaDescription: 'Web etkinlikleri takip ve analitik hizmeti. Özel etkinlik takibi, dönüşüm analizi ve etkinlik bazlı segmentasyon ile web sitenizin etkinlik performansını optimize edin.',
      keywords: ['etkinlik takibi', 'event tracking', 'dönüşüm takibi', 'etkinlik analizi', 'web etkinlikleri'],
      ogImage: '/services/web-events-analytics.png'
    },
    content: `
# Web Etkinlikleri Takip ve Analitik Hizmeti

Web sitenizdeki kullanıcı etkinliklerini detaylı takip edin ve dönüşümlerinizi analiz edin. Winnomi AI ile özel etkinlikler oluşturun ve performansınızı optimize edin.

## Web Etkinlikleri Analitiği Nedir?

Web etkinlikleri analitiği, kullanıcıların web sitenizde gerçekleştirdiği belirli eylemleri takip etme ve analiz etme sürecidir. Bu analiz, dönüşüm hunilerini optimize etmek ve kullanıcı davranışlarını anlamak için kritik öneme sahiptir.

## Temel Etkinlikler

### Standart Etkinlikler
- Sayfa görüntüleme
- Buton tıklama
- Form gönderimi
- Video oynatma
- Dosya indirme

### Özel Etkinlikler
- Özel buton tıklamaları
- Özel form alanları
- Özel içerik etkileşimleri
- Özel dönüşüm noktaları

## Özellikler

### 1. Özel Etkinlik Oluşturma

İhtiyacınıza özel etkinlikler oluşturun:
- Buton tıklama takibi
- Form alan takibi
- Scroll derinliği takibi
- Özel dönüşüm noktaları

### 2. Etkinlik Bazlı Analiz

Etkinliklerinizin performansını analiz edin:
- Etkinlik sayıları
- Etkinlik oranları
- Dönüşüm oranları
- Trend analizi

### 3. Etkinlik Hunileri

Dönüşüm hunilerinizi oluşturun:
- Çok adımlı huniler
- Etkinlik bazlı segmentasyon
- Dönüşüm optimizasyonu
- A/B test entegrasyonu

## Faydalar

### Dönüşüm Optimizasyonu

Etkinlik takibi ile:
- Dönüşüm hunilerini optimize edin
- Etkinlik bazlı segmentasyon yapın
- A/B test sonuçlarını ölçün

### Kullanıcı Davranış Analizi

Etkinlik analizi ile:
- Kullanıcı davranışlarını anlayın
- Etkileşim noktalarını keşfedin
- Kullanıcı yolculuğunu optimize edin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-channels-analytics',
    slug: 'web-kanallar-analitik',
    title: 'Web Trafik Kanalları Analitik Hizmeti',
    shortDescription: 'Web sitenize gelen trafiğin kanallarını analiz edin, kanal performansını ölçün ve trafik kaynaklarını optimize edin.',
    description: 'Trafik kanallarınızın performansını detaylı analiz edin. Organik arama, sosyal medya, e-posta, reklam ve daha fazlası.',
    icon: '🔗',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Trafik kanalı performans analizi',
      'Organik arama trafiği',
      'Sosyal medya trafiği',
      'E-posta kampanya trafiği',
      'Reklam trafiği analizi',
      'Referral trafik analizi',
      'Direkt trafik analizi',
      'Kanal bazlı dönüşüm analizi'
    ],
    benefits: [
      'En etkili trafik kanallarınızı keşfedin',
      'Trafik kaynaklarınızı çeşitlendirin',
      'Kanal ROI\'nizi hesaplayın',
      'Pazarlama stratejinizi optimize edin',
      'Bütçe tahsisini iyileştirin'
    ],
    metrics: [
      'Kanal Trafiği',
      'Kanal Dönüşüm Oranı',
      'Kanal ROI',
      'Kanal Müşteri Değeri',
      'Kanal Bounce Rate',
      'Kanal Oturum Süresi',
      'Kanal Sayfa Görüntülenme',
      'Kanal Trend Analizi'
    ],
    seo: {
      metaDescription: 'Web trafik kanalları analitik hizmeti. Organik arama, sosyal medya, e-posta ve reklam trafiği analizi ile trafik kaynaklarınızı optimize edin.',
      keywords: ['trafik kanalları', 'kanal analizi', 'organik trafik', 'sosyal medya trafiği', 'e-posta trafiği', 'trafik kaynakları'],
      ogImage: '/services/web-channels-analytics.png'
    },
    content: `
# Web Trafik Kanalları Analitik Hizmeti

Web sitenize gelen trafiğin kanallarını detaylı analiz edin ve pazarlama stratejinizi optimize edin. Winnomi AI ile en etkili trafik kanallarınızı keşfedin ve ROI'nizi artırın.

## Trafik Kanalları Analitiği Nedir?

Trafik kanalları analitiği, web sitenize gelen trafiğin kaynaklarını kategorize etme ve performanslarını analiz etme sürecidir. Bu analiz, pazarlama stratejinizi optimize etmek ve ROI'nizi artırmak için kritik öneme sahiptir.

## Trafik Kanalları

### Organik Arama
- Google arama trafiği
- Diğer arama motorları
- SEO performansı
- Anahtar kelime analizi

### Sosyal Medya
- Facebook trafiği
- Instagram trafiği
- Twitter trafiği
- LinkedIn trafiği
- Diğer platformlar

### E-posta
- E-posta kampanya trafiği
- Newsletter trafiği
- Otomatik e-posta trafiği

### Reklam
- Google Ads trafiği
- Facebook Ads trafiği
- Diğer reklam platformları

## Özellikler

### 1. Kanal Performans Analizi

Her kanalın performansını detaylı analiz edin:
- Trafik hacmi
- Dönüşüm oranları
- ROI hesaplamaları
- Müşteri değeri

### 2. Kanal Karşılaştırması

Kanalları birbiriyle karşılaştırın:
- Performans karşılaştırması
- ROI karşılaştırması
- Trend analizi
- Benchmark analizi

### 3. Kanal Optimizasyonu

Kanallarınızı optimize edin:
- Bütçe tahsisi önerileri
- Performans iyileştirme önerileri
- Yeni kanal fırsatları

## Faydalar

### Pazarlama Stratejisi Optimizasyonu

Kanal analizi ile:
- En etkili kanallarınızı belirleyin
- Bütçe tahsisini optimize edin
- Pazarlama stratejinizi iyileştirin

### ROI Artışı

Kanal performans analizi ile:
- ROI'nizi artırın
- Kaynak tahsisini optimize edin
- Pazarlama verimliliğini iyileştirin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-social-to-site',
    slug: 'sosyalden-siteye-analitik',
    title: 'Sosyal Medyadan Siteye Trafik Analitik Hizmeti',
    shortDescription: 'Sosyal medya platformlarından web sitenize gelen trafiği analiz edin ve sosyal medya stratejinizi optimize edin.',
    description: 'Sosyal medya trafiğinizi detaylı analiz edin. Platform bazlı trafik, içerik performansı, dönüşüm analizi ve daha fazlası.',
    icon: '📱',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Platform bazlı trafik analizi',
      'İçerik bazlı trafik analizi',
      'Sosyal medya dönüşüm analizi',
      'Referral trafik takibi',
      'UTM parametre analizi',
      'Sosyal medya ROI hesaplama',
      'İçerik performans karşılaştırması',
      'Trend analizi'
    ],
    benefits: [
      'Sosyal medya stratejinizi optimize edin',
      'En etkili içeriklerinizi keşfedin',
      'Sosyal medya ROI\'nizi hesaplayın',
      'Platform performansını karşılaştırın',
      'Dönüşüm oranlarınızı artırın'
    ],
    metrics: [
      'Sosyal medya Trafiği',
      'Platform Bazlı Trafik',
      'İçerik Bazlı Trafik',
      'Dönüşüm Oranı',
      'Sosyal medya ROI',
      'Ortalama Oturum Süresi',
      'Sayfa Görüntülenme',
      'Bounce Rate'
    ],
    seo: {
      metaDescription: 'sosyal medyadan siteye trafik analitik hizmeti. Platform bazlı trafik analizi, içerik performansı ve sosyal medya ROI hesaplama ile sosyal medya stratejinizi optimize edin.',
      keywords: ['sosyal medya trafiği', 'sosyal medya analitik', 'sosyal medya ROI', 'platform trafiği', 'referral trafik'],
      ogImage: '/services/social-to-site-analytics.png'
    },
    content: `
# Sosyal medyadan Siteye Trafik Analitik Hizmeti

Sosyal medya platformlarından web sitenize gelen trafiği detaylı analiz edin ve sosyal medya stratejinizi optimize edin. Winnomi AI ile platform performansınızı karşılaştırın ve ROI'nizi artırın.

## Sosyal medyadan Siteye Trafik Analitiği Nedir?

Sosyal medyadan siteye trafik analitiği, sosyal medya platformlarından web sitenize gelen trafiği ölçme ve analiz etme sürecidir. Bu analiz, sosyal medya stratejinizi optimize etmek ve ROI'nizi artırmak için kritik öneme sahiptir.

## Temel Metrikler

### Platform Bazlı Trafik
- Instagram trafiği
- Facebook trafiği
- Twitter trafiği
- LinkedIn trafiği
- TikTok trafiği
- Diğer platformlar

### İçerik Performansı
- İçerik bazlı trafik
- Paylaşım performansı
- Link tıklama oranları
- Dönüşüm oranları

## Özellikler

### 1. Platform Performans Analizi

Her platformun trafik performansını analiz edin:
- Trafik hacmi
- Dönüşüm oranları
- ROI hesaplamaları
- Trend analizi

### 2. İçerik Performans Analizi

Sosyal medya içeriklerinizin trafik performansını analiz edin:
- İçerik bazlı trafik
- Link tıklama oranları
- Dönüşüm oranları
- Performans karşılaştırması

### 3. UTM Parametre Analizi

UTM parametrelerinizi analiz edin:
- Kampanya performansı
- İçerik performansı
- Kaynak analizi
- Ortam analizi

## Faydalar

### Sosyal medya Stratejisi Optimizasyonu

Sosyal medya trafik analizi ile:
- En etkili platformlarınızı belirleyin
- İçerik stratejinizi optimize edin
- Platform performansını karşılaştırın

### ROI Artışı

Sosyal medya ROI analizi ile:
- ROI'nizi hesaplayın
- Platform yatırımlarını optimize edin
- Pazarlama verimliliğini iyileştirin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'web-attribution',
    slug: 'web-attribution-analitik',
    title: 'Web Attribution ve Dönüşüm Yolu Analitik Hizmeti',
    shortDescription: 'Kullanıcıların dönüşüm yolculuğunu analiz edin, attribution modellerini kullanın ve pazarlama etkisini ölçün.',
    description: 'Multi-touch attribution ile dönüşüm yolculuğunu analiz edin. First-touch, last-touch, linear ve time-decay modelleri.',
    icon: '🎯',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/20',
    category: 'web-analytics',
    comingSoon: true,
    features: [
      'Multi-touch attribution',
      'First-touch attribution',
      'Last-touch attribution',
      'Linear attribution',
      'Time-decay attribution',
      'Dönüşüm yolu analizi',
      'Pazarlama etkisi ölçümü',
      'Kanal kredisi analizi'
    ],
    benefits: [
      'Dönüşüm yolculuğunu anlayın',
      'Pazarlama etkisini ölçün',
      'Kanal kredisini doğru dağıtın',
      'ROI hesaplamalarınızı iyileştirin',
      'Pazarlama stratejinizi optimize edin'
    ],
    metrics: [
      'Attribution Kredisi',
      'Dönüşüm Yolu Uzunluğu',
      'Dönüşüm Süresi',
      'Kanal Etkileşim Sayısı',
      'First-Touch Kredisi',
      'Last-Touch Kredisi',
      'Assisted Conversions',
      'Attribution Model Karşılaştırması'
    ],
    seo: {
      metaDescription: 'Web attribution ve dönüşüm yolu analitik hizmeti. Multi-touch attribution, dönüşüm yolculuğu analizi ve pazarlama etkisi ölçümü ile pazarlama stratejinizi optimize edin.',
      keywords: ['attribution', 'dönüşüm yolu', 'multi-touch attribution', 'pazarlama etkisi', 'kanal kredisi', 'attribution modeli'],
      ogImage: '/services/web-attribution-analytics.png'
    },
    content: `
# Web Attribution ve Dönüşüm Yolu Analitik Hizmeti

Kullanıcıların dönüşüm yolculuğunu detaylı analiz edin ve pazarlama etkisini ölçün. Winnomi AI ile multi-touch attribution modellerini kullanın ve stratejinizi optimize edin.

## Attribution Analitiği Nedir?

Attribution analitiği, bir dönüşümün hangi pazarlama kanallarına ve etkileşimlere atfedileceğini belirleme sürecidir. Bu analiz, pazarlama etkisini doğru ölçmek ve ROI hesaplamalarını iyileştirmek için kritik öneme sahiptir.

## Attribution Modelleri

### First-Touch Attribution
İlk temas noktasına tüm krediyi verir.

### Last-Touch Attribution
Son temas noktasına tüm krediyi verir.

### Linear Attribution
Tüm temas noktalarına eşit kredi verir.

### Time-Decay Attribution
Zamana yakın temas noktalarına daha fazla kredi verir.

### Position-Based Attribution
İlk ve son temas noktalarına daha fazla kredi verir.

## Özellikler

### 1. Multi-Touch Attribution

Çoklu temas noktalarını analiz edin:
- Dönüşüm yolu analizi
- Kanal etkileşim sayısı
- Dönüşüm süresi
- Kanal kombinasyonları

### 2. Attribution Model Karşılaştırması

Farklı attribution modellerini karşılaştırın:
- Model bazlı kredi dağılımı
- Kanal performans karşılaştırması
- ROI hesaplama farklılıkları

### 3. Dönüşüm Yolu Analizi

Kullanıcı yolculuğunu analiz edin:
- Yolculuk uzunluğu
- Yolculuk süresi
- Kanal sıralaması
- Dönüşüm noktaları

## Faydalar

### Pazarlama Etkisi Ölçümü

Attribution analizi ile:
- Pazarlama etkisini doğru ölçün
- Kanal kredisini doğru dağıtın
- ROI hesaplamalarınızı iyileştirin

### Strateji Optimizasyonu

Dönüşüm yolu analizi ile:
- En etkili kanal kombinasyonlarını keşfedin
- Pazarlama stratejinizi optimize edin
- Bütçe tahsisini iyileştirin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'ads-overview',
    slug: 'reklam-analitik',
    title: 'Reklam Analitik ve Performans Takip Hizmeti',
    shortDescription: 'Tüm reklam kampanyalarınızın performansını tek bir platformda takip edin, ROI hesaplayın ve reklam stratejinizi optimize edin.',
    description: 'Reklam kampanyalarınızın performansını detaylı analiz edin. Platform bazlı analiz, kampanya performansı, ROI hesaplama ve daha fazlası.',
    icon: '📢',
    color: 'text-red-500',
    bgColor: 'bg-red-500/20',
    category: 'advertising',
    comingSoon: true,
    features: [
      'Platform bazlı reklam analizi',
      'Kampanya performans takibi',
      'Reklam grubu analizi',
      'Reklam performans metrikleri',
      'ROI ve ROAS hesaplama',
      'Maliyet analizi',
      'Dönüşüm takibi',
      'Rekabet analizi'
    ],
    benefits: [
      'Reklam performansınızı optimize edin',
      'ROI\'nizi artırın',
      'Bütçe tahsisini iyileştirin',
      'Kampanya stratejinizi güçlendirin',
      'Maliyetleri düşürün'
    ],
    metrics: [
      'Impressions',
      'Clicks',
      'CTR',
      'CPC',
      'CPM',
      'CPA',
      'ROAS',
      'ROI'
    ],
    seo: {
      metaDescription: 'Reklam analitik ve performans takip hizmeti. Platform bazlı reklam analizi, kampanya performansı, ROI hesaplama ve reklam stratejisi optimizasyonu.',
      keywords: ['reklam analitik', 'reklam performansı', 'ROI hesaplama', 'ROAS', 'kampanya analizi', 'reklam metrikleri'],
      ogImage: '/services/ads-overview-analytics.png'
    },
    content: `
# Reklam Analitik ve Performans Takip Hizmeti

Tüm reklam kampanyalarınızın performansını tek bir platformda takip edin ve reklam stratejinizi optimize edin. Winnomi AI ile ROI'nizi artırın ve maliyetlerinizi düşürün.

## Reklam Analitiği Nedir?

Reklam analitiği, reklam kampanyalarınızın performansını ölçme ve analiz etme sürecidir. Bu analiz, reklam stratejinizi optimize etmek ve ROI'nizi artırmak için kritik öneme sahiptir.

## Temel Metrikler

### Performans Metrikleri
- Impressions (Gösterim)
- Clicks (Tıklama)
- CTR (Tıklama Oranı)
- CPC (Tıklama Başına Maliyet)
- CPM (1000 Gösterim Başına Maliyet)

### Dönüşüm Metrikleri
- Conversions (Dönüşüm)
- CPA (Dönüşüm Başına Maliyet)
- Conversion Rate (Dönüşüm Oranı)
- ROAS (Reklam Harcaması Geri Dönüşü)
- ROI (Yatırım Getirisi)

## Özellikler

### 1. Platform Bazlı Analiz

Her reklam platformunun performansını analiz edin:
- Facebook Ads
- Google Ads
- Instagram Ads
- LinkedIn Ads
- Twitter Ads
- Diğer platformlar

### 2. Kampanya Performans Takibi

Kampanyalarınızın performansını detaylı takip edin:
- Kampanya bazlı metrikler
- Reklam grubu performansı
- Reklam performansı
- A/B test sonuçları

### 3. ROI ve ROAS Hesaplama

Reklam yatırımınızın geri dönüşünü hesaplayın:
- ROI hesaplamaları
- ROAS hesaplamaları
- Kanal bazlı ROI
- Kampanya bazlı ROI

## Faydalar

### Reklam Optimizasyonu

Reklam analitiği ile:
- En etkili kampanyalarınızı belirleyin
- Reklam performansını optimize edin
- Bütçe tahsisini iyileştirin

### ROI Artışı

ROI analizi ile:
- ROI'nizi artırın
- Maliyetleri düşürün
- Reklam verimliliğini iyileştirin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'ads-to-site',
    slug: 'reklamdan-siteye-akis',
    title: 'Reklamdan Siteye Akış Analitik Hizmeti',
    shortDescription: 'Reklam kampanyalarınızdan web sitenize gelen trafiği analiz edin ve reklam performansınızı optimize edin.',
    description: 'Reklam trafiğinizi detaylı analiz edin. Reklam kaynaklı ziyaretçiler, dönüşüm oranları, trafik kalitesi ve daha fazlası.',
    icon: '📈',
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/20',
    category: 'advertising',
    comingSoon: true,
    features: [
      'Reklam trafiği analizi',
      'Kampanya bazlı trafik',
      'Reklam bazlı trafik',
      'Dönüşüm oranı analizi',
      'Trafik kalitesi analizi',
      'Sayfa performans analizi',
      'Kullanıcı davranış analizi',
      'ROI hesaplama'
    ],
    benefits: [
      'Reklam trafiğini optimize edin',
      'Dönüşüm oranlarınızı artırın',
      'Reklam ROI\'nizi hesaplayın',
      'Kampanya performansını iyileştirin',
      'Trafik kalitesini artırın'
    ],
    metrics: [
      'Reklam Trafiği',
      'Kampanya Trafiği',
      'Dönüşüm Oranı',
      'Trafik Kalitesi',
      'Ortalama Oturum Süresi',
      'Sayfa Görüntülenme',
      'Bounce Rate',
      'ROI'
    ],
    seo: {
      metaDescription: 'Reklamdan siteye akış analitik hizmeti. Reklam trafiği analizi, dönüşüm oranları ve trafik kalitesi ile reklam performansınızı optimize edin.',
      keywords: ['reklam trafiği', 'reklam dönüşümü', 'kampanya trafiği', 'reklam ROI', 'trafik kalitesi'],
      ogImage: '/services/ads-to-site-analytics.png'
    },
    content: `
# Reklamdan Siteye Akış Analitik Hizmeti

Reklam kampanyalarınızdan web sitenize gelen trafiği detaylı analiz edin ve reklam performansınızı optimize edin. Winnomi AI ile dönüşüm oranlarınızı artırın ve ROI'nizi maksimize edin.

## Reklamdan Siteye Akış Analitiği Nedir?

Reklamdan siteye akış analitiği, reklam kampanyalarınızdan web sitenize gelen trafiği ölçme ve analiz etme sürecidir. Bu analiz, reklam performansını optimize etmek ve dönüşüm oranlarını artırmak için kritik öneme sahiptir.

## Temel Metrikler

### Trafik Metrikleri
- Reklam trafiği hacmi
- Kampanya bazlı trafik
- Reklam bazlı trafik
- Trafik trendleri

### Dönüşüm Metrikleri
- Dönüşüm oranları
- Dönüşüm sayıları
- Dönüşüm değeri
- ROI hesaplamaları

## Özellikler

### 1. Reklam Trafiği Analizi

Reklam trafiğinizi detaylı analiz edin:
- Trafik hacmi
- Trafik kalitesi
- Trafik trendleri
- Platform bazlı trafik

### 2. Dönüşüm Analizi

Reklam trafiğinin dönüşüm performansını analiz edin:
- Dönüşüm oranları
- Dönüşüm sayıları
- Dönüşüm değeri
- Dönüşüm hunileri

### 3. Trafik Kalitesi Analizi

Reklam trafiğinin kalitesini ölçün:
- Bounce rate
- Oturum süresi
- Sayfa görüntülenme
- Kullanıcı davranışı

## Faydalar

### Reklam Optimizasyonu

Reklam trafiği analizi ile:
- En etkili kampanyalarınızı belirleyin
- Trafik kalitesini artırın
- Dönüşüm oranlarını optimize edin

### ROI Artışı

Dönüşüm analizi ile:
- ROI'nizi artırın
- Reklam verimliliğini iyileştirin
- Bütçe tahsisini optimize edin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'ads-funnel',
    slug: 'reklam-funnel-analitik',
    title: 'Reklam Funnel ve Dönüşüm Yolu Analitik Hizmeti',
    shortDescription: 'Reklam kampanyalarınızın dönüşüm hunilerini analiz edin, darboğazları tespit edin ve dönüşüm oranlarınızı optimize edin.',
    description: 'Reklam dönüşüm hunilerinizi detaylı analiz edin. Funnel aşamaları, darboğazlar, dönüşüm oranları ve optimizasyon önerileri.',
    icon: '🔽',
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/20',
    category: 'advertising',
    comingSoon: true,
    features: [
      'Funnel aşama analizi',
      'Darboğaz tespiti',
      'Dönüşüm oranı analizi',
      'Aşama bazlı kayıp analizi',
      'Funnel optimizasyon önerileri',
      'A/B test entegrasyonu',
      'Kampanya bazlı funnel',
      'Zaman bazlı funnel analizi'
    ],
    benefits: [
      'Dönüşüm hunilerini optimize edin',
      'Darboğazları tespit edin',
      'Dönüşüm oranlarınızı artırın',
      'Kayıp noktalarını belirleyin',
      'ROI\'nizi maksimize edin'
    ],
    metrics: [
      'Funnel Aşama Oranları',
      'Darboğaz Oranları',
      'Dönüşüm Oranı',
      'Kayıp Oranları',
      'Aşama Süreleri',
      'Funnel Tamamlama Oranı',
      'A/B Test Sonuçları',
      'Optimizasyon Potansiyeli'
    ],
    seo: {
      metaDescription: 'Reklam funnel ve dönüşüm yolu analitik hizmeti. Funnel aşama analizi, darboğaz tespiti ve dönüşüm optimizasyonu ile reklam performansınızı artırın.',
      keywords: ['reklam funnel', 'dönüşüm hunisi', 'funnel analizi', 'darboğaz tespiti', 'dönüşüm optimizasyonu'],
      ogImage: '/services/ads-funnel-analytics.png'
    },
    content: `
# Reklam Funnel ve Dönüşüm Yolu Analitik Hizmeti

Reklam kampanyalarınızın dönüşüm hunilerini detaylı analiz edin ve dönüşüm oranlarınızı optimize edin. Winnomi AI ile darboğazları tespit edin ve ROI'nizi maksimize edin.

## Reklam Funnel Analitiği Nedir?

Reklam funnel analitiği, kullanıcıların reklamdan dönüşüme kadar olan yolculuğunu aşama aşama analiz etme sürecidir. Bu analiz, dönüşüm hunilerini optimize etmek ve darboğazları tespit etmek için kritik öneme sahiptir.

## Funnel Aşamaları

### Farkındalık
- Reklam görüntülenme
- Reklam tıklama
- Web sitesi ziyareti

### İlgi
- Sayfa görüntülenme
- İçerik tüketimi
- Etkileşim

### Değerlendirme
- Ürün/hizmet inceleme
- Karşılaştırma
- Bilgi toplama

### Dönüşüm
- Form doldurma
- Satın alma
- Kayıt

## Özellikler

### 1. Funnel Aşama Analizi

Her funnel aşamasının performansını analiz edin:
- Aşama bazlı ziyaretçi sayıları
- Aşama bazlı dönüşüm oranları
- Aşama bazlı kayıp oranları
- Aşama bazlı süreler

### 2. Darboğaz Tespiti

Funnel'daki darboğazları tespit edin:
- Yüksek kayıp oranları
- Uzun süreler
- Düşük dönüşüm oranları
- Optimizasyon fırsatları

### 3. Funnel Optimizasyonu

Funnel'ınızı optimize edin:
- Optimizasyon önerileri
- A/B test önerileri
- Performans iyileştirme stratejileri

## Faydalar

### Dönüşüm Optimizasyonu

Funnel analizi ile:
- Darboğazları tespit edin
- Dönüşüm oranlarınızı artırın
- Kayıp noktalarını belirleyin

### ROI Artışı

Funnel optimizasyonu ile:
- ROI'nizi maksimize edin
- Reklam verimliliğini iyileştirin
- Dönüşüm maliyetlerini düşürün

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  },
  {
    id: 'ads-conversions',
    slug: 'reklam-donusumler',
    title: 'Reklam Dönüşümleri ve ROI Analitik Hizmeti',
    shortDescription: 'Reklam kampanyalarınızın dönüşümlerini detaylı analiz edin, ROI hesaplayın ve reklam performansınızı optimize edin.',
    description: 'Reklam dönüşümlerinizi detaylı takip edin. Dönüşüm türleri, dönüşüm değeri, ROI hesaplamaları ve kampanya performansı.',
    icon: '🎯',
    color: 'text-green-500',
    bgColor: 'bg-green-500/20',
    category: 'advertising',
    comingSoon: true,
    features: [
      'Dönüşüm takibi',
      'Dönüşüm türü analizi',
      'Dönüşüm değeri analizi',
      'ROI hesaplama',
      'ROAS hesaplama',
      'Kampanya bazlı dönüşüm',
      'Reklam bazlı dönüşüm',
      'Dönüşüm trend analizi'
    ],
    benefits: [
      'Dönüşümlerinizi optimize edin',
      'ROI\'nizi artırın',
      'Kampanya performansını iyileştirin',
      'Dönüşüm maliyetlerini düşürün',
      'Reklam verimliliğini artırın'
    ],
    metrics: [
      'Toplam Dönüşüm',
      'Dönüşüm Oranı',
      'Dönüşüm Değeri',
      'CPA',
      'ROI',
      'ROAS',
      'Dönüşüm Trendleri',
      'Kampanya Performansı'
    ],
    seo: {
      metaDescription: 'Reklam dönüşümleri ve ROI analitik hizmeti. Dönüşüm takibi, ROI hesaplama, ROAS analizi ve reklam performans optimizasyonu.',
      keywords: ['reklam dönüşümü', 'ROI hesaplama', 'ROAS', 'CPA', 'dönüşüm takibi', 'reklam performansı'],
      ogImage: '/services/ads-conversions-analytics.png'
    },
    content: `
# Reklam Dönüşümleri ve ROI Analitik Hizmeti

Reklam kampanyalarınızın dönüşümlerini detaylı analiz edin ve ROI'nizi hesaplayın. Winnomi AI ile reklam performansınızı optimize edin ve yatırım getirinizi maksimize edin.

## Reklam Dönüşümleri Analitiği Nedir?

Reklam dönüşümleri analitiği, reklam kampanyalarınızın ürettiği dönüşümleri ölçme ve analiz etme sürecidir. Bu analiz, reklam performansını optimize etmek ve ROI'nizi artırmak için kritik öneme sahiptir.

## Temel Metrikler

### Dönüşüm Metrikleri
- Toplam dönüşüm sayısı
- Dönüşüm oranı
- Dönüşüm değeri
- Dönüşüm türleri

### ROI Metrikleri
- ROI (Yatırım Getirisi)
- ROAS (Reklam Harcaması Geri Dönüşü)
- CPA (Dönüşüm Başına Maliyet)
- Profit Margin

## Özellikler

### 1. Dönüşüm Takibi

Dönüşümlerinizi detaylı takip edin:
- Dönüşüm sayıları
- Dönüşüm türleri
- Dönüşüm değeri
- Dönüşüm trendleri

### 2. ROI Hesaplama

Reklam yatırımınızın geri dönüşünü hesaplayın:
- ROI hesaplamaları
- ROAS hesaplamaları
- Kanal bazlı ROI
- Kampanya bazlı ROI

### 3. Dönüşüm Analizi

Dönüşümlerinizi detaylı analiz edin:
- Kampanya bazlı dönüşüm
- Reklam bazlı dönüşüm
- Zaman bazlı dönüşüm
- Segment bazlı dönüşüm

## Faydalar

### Dönüşüm Optimizasyonu

Dönüşüm analizi ile:
- En etkili kampanyalarınızı belirleyin
- Dönüşüm oranlarınızı artırın
- Dönüşüm maliyetlerini düşürün

### ROI Artışı

ROI analizi ile:
- ROI'nizi maksimize edin
- Reklam verimliliğini iyileştirin
- Yatırım kararlarınızı optimize edin

## Yakında

Bu hizmet yakında kullanıma sunulacaktır. Haberdar olmak için bizi takip edin!
    `
  }
];

// Export services as a getter function (default to Turkish)
export const services = getServices();

export const getServiceBySlug = (slug: string, i18nInstance?: any): Service | undefined => {
  return getServices(i18nInstance).find(service => service.slug === slug);
};

export const getServicesByCategory = (category: string, i18nInstance?: any): Service[] => {
  return getServices(i18nInstance).filter(service => service.category === category);
};

export const getRelatedServices = (currentService: Service, limit: number = 3, i18nInstance?: any): Service[] => {
  const servicesList = getServices(i18nInstance);
  return servicesList
    .filter(service =>
      service.id !== currentService.id &&
      (service.category === currentService.category ||
        service.category.includes(currentService.category.split('-')[0]))
    )
    .slice(0, limit);
};

